/**
 * Demo backend for the directory — the answers `backend/api` master does not give yet.
 *
 * ⚠ **EVERYTHING THIS FILE PRODUCES IS INVENTED**, except where a comment says otherwise.
 * It exists because of one decision, taken on 16 September 2026:
 *
 * > David Culebras: *«No las uses. Pinta el frontend pero no lo enganches con el backend.
 * > Primero tengo que reunirme con Dani y ver el alcance … no estará en BBDD. Serán índices
 * > de ElasticSearch, pero no hagas nada del backend de momento.»*
 * > Daniel Samper: *"these comms panels, for now, should only carry mockup data here."*
 *
 * So the API stays on `master` and the page has to stand on its own.
 *
 * ## What is missing, measured against master on 17 Sep 2026
 *
 * Two different kinds of gap, which is why there are two kinds of answer below.
 *
 * **1. Endpoints that do not exist.** Verified with a real token against a real master
 * build: every one of these answers **404**.
 *
 * | endpoint | what goes blank without it |
 * |---|---|
 * | `GET /ratings/organizations` | the As client / As provider columns and their facets |
 * | `GET /ratings/people` | the Internal tab's Peer rating column |
 * | `GET /ratings/organizations/{id}/reviews` | the panel's Rating & Feedback tab |
 * | `PUT`/`DELETE /ratings/organizations/{id}/mine` | the Rate button errors |
 * | `GET /organizationOffers` | Organization offers, plus three facets |
 * | `GET /peoplePermissions` | the admin-only Permissions column |
 * | `GET /directoryLookup` | step one of Add contact cannot answer at all |
 *
 * **2. Fields that are missing from endpoints that DO exist.** `/people/directory` answers
 * 200 and simply carries thirteen fewer fields; `getPartners` carries four fewer.
 *
 * ## How it disappears
 *
 * Neither kind is faked unconditionally.
 *
 * - A stand-in is produced **only after the real call has answered 404**. The day David
 *   deploys `/ratings/organizations`, the 404 stops happening and the real numbers appear.
 *   No front-end change, no flag to remember.
 * - A field is filled **only when the response does not carry it**. The day the server
 *   sends `workingHoursFrom`, the seed steps aside for that field alone.
 *
 * So this file is self-cancelling: it fades out endpoint by endpoint, field by field, as
 * the back end catches up. To remove it entirely, delete the folder and the one word
 * `directoryDemoBackendInterceptor` in {@link ../../app/app.config.ts}.
 *
 * ## Rules it follows
 *
 * - **Nothing invented is ever sent to the API.** Every write below is remembered in this
 *   module and nowhere else, and is gone on reload.
 * - **Deterministic.** Same id, same answer, on every render and every reload. A seed built
 *   on `Math.random()` would reshuffle a rating while a reader looked at it.
 * - **Shaped exactly like the server's DTO**, field for field, so the switch-over is a
 *   deletion and not a rewrite. Each seed names the Java class it imitates.
 * - **It records that it fired** ({@link demoBackendUsed}), so the page can say so on screen.
 *   A reader must never take these numbers for real ones.
 */

import { signal } from '@angular/core';
import { seedCapabilities, seedStatusMessage } from 'src/app/pages/directories2/shared/directory-capabilities';
import { seedAlreadyAContact, seedGlotwiseAccount, seedListedOnMarketplace } from 'src/app/pages/directories2/shared/directory-glotwise';
import { INVENTED_DATA_ENABLED } from './invented-data';

// Re-exported so the directory's own files keep one import for everything demo-related.
export { INVENTED_DATA_ENABLED };

/**
 * The master switch.
 *
 * Leaving it `true` is safe even in production: nothing is substituted until an endpoint
 * answers 404, and no field is filled that the server already sends. Set it to `false` to
 * see exactly what the page looks like on a bare master API.
 *
 * That claim was briefly untrue. Between 18 and 21 September the page invented three things —
 * what people can do, status messages and the marketplace overlap — **without asking for them
 * first**, so they could not be displaced by a server that started answering. They now go
 * through a request like everything else (21/09), which is what makes the sentence above hold
 * again. **Nothing may be added to this file that has not been asked for over HTTP.**
 */
export const DEMO_BACKEND_ENABLED = true;


/**
 * Which parts of the page are currently running on invented data.
 *
 * Written by the interceptor the first time it substitutes anything, read by the page's
 * "Demo data" pill. A set rather than a boolean so the pill can list what is affected:
 * once ratings are real and offers are not, the reader should be told precisely that.
 */
export const demoBackendUsed = signal<string[]>([]);

/** Records one kind of substitution, at most once. Order of first use is preserved. */
export function noteDemoData(what: string): void {
  const seen = demoBackendUsed();
  if (!seen.includes(what)) demoBackendUsed.set([...seen, what]);
}

// ─────────────────────────────────────────────────────────────────────────────
// The deterministic core
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A stable pseudo-random integer from a string.
 *
 * The same function as `directory-connections.ts` uses, and for the same reason: a seed
 * has to survive a re-render. Salted per use so one id can answer many questions without
 * every answer moving together.
 */
function hashOf(text: string): number {
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) | 0;
  return Math.abs(hash);
}

/** `0 <= pick(...) < max`, stable for a given id and salt. */
function pick(id: string | number, salt: string, max: number): number {
  return hashOf(`${salt}:${id}`) % max;
}

/** One element of `list`, chosen stably. */
function one<T>(id: string | number, salt: string, list: readonly T[]): T {
  return list[pick(id, salt, list.length)];
}

/** `count` distinct elements of `list`, chosen stably, in the list's own order. */
function some<T>(id: string | number, salt: string, list: readonly T[], count: number): T[] {
  const taken = new Set<number>();
  for (let i = 0; taken.size < Math.min(count, list.length) && i < list.length * 4; i++) {
    taken.add(pick(id, `${salt}#${i}`, list.length));
  }
  return list.filter((_, i) => taken.has(i));
}

// ─────────────────────────────────────────────────────────────────────────────
// Ratings — GET /ratings/**  (imitates RatingsController)
// ─────────────────────────────────────────────────────────────────────────────

/** Invented reviewers. Deliberately not real colleagues: nobody's name on an opinion they never gave. */
const RATERS = [
  'Marta Ibáñez', 'Tomas Novak', 'Aiko Tanaka', 'Paul Berger', 'Lucia Ferrari',
  'Henrik Sørensen', 'Noor Haddad', 'Grzegorz Mazur', 'Carla Pinto', 'Sven Lindqvist',
];

/** Invented comments, in the register a professional would actually write one. */
const COMMENTS = [
  'Delivered ahead of the deadline twice in a row. Clear questions, no surprises.',
  'Good work overall. The first batch needed a second pass on terminology.',
  'Responsive and easy to plan around, even at short notice.',
  'Quality is consistent. I would use them again for the same language pair.',
  'Communication could be quicker, but the files themselves were clean.',
  '',
];

/** The six score columns, in the order `organizationscore` holds them. */
type Scores = {
  deadlines: number | null;
  cooperation: number | null;
  personabilityClient: number | null;
  quality: number | null;
  onTime: number | null;
  personabilityProvider: number | null;
};

/** One row, shaped like `RatingsController.Review`. */
export interface DemoReview extends Scores {
  id: number;
  rater: string;
  createdAt: string;
  comment: string;
  /** 'client' | 'provider' | 'both' — which block the row sits under. */
  role: string;
  clientAverage: number | null;
  vendorAverage: number | null;
  mine: boolean;
}

/**
 * The reader's own rating of an organization, as typed into the form.
 *
 * In memory only. `PUT .../mine` does not exist on master, so the write is remembered here
 * and read back by the reviews seed — which is the whole point: the form has to behave like
 * a form, and a star that springs back on save reads as a bug rather than as a stub.
 * Gone on reload, and never sent anywhere.
 */
const myRatings = new Map<number, Partial<Scores> & { comment?: string }>();

export function rememberMyRating(organizationId: number, body: any): void {
  myRatings.set(organizationId, {
    deadlines: num(body?.deadlines),
    cooperation: num(body?.cooperation),
    personabilityClient: num(body?.personabilityClient),
    quality: num(body?.quality),
    onTime: num(body?.onTime),
    personabilityProvider: num(body?.personabilityProvider),
    comment: typeof body?.comment === 'string' ? body.comment : '',
  });
}

export function forgetMyRating(organizationId: number): void {
  myRatings.delete(organizationId);
}

/** Only whole 1–5 survives, matching the `numeric(1,0)` columns the real endpoint writes to. */
function num(value: any): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n >= 1 && n <= 5 ? Math.round(n) : null;
}

/** Mean of the scores that are present, to one decimal; null when none are. */
function meanOf(values: (number | null)[]): number | null {
  const kept = values.filter((v): v is number => v != null);
  if (!kept.length) return null;
  return Math.round((kept.reduce((a, b) => a + b, 0) / kept.length) * 10) / 10;
}

/**
 * Every review on one organization, invented but stable.
 *
 * Most partners carry nothing, which is the truth on Dev and in production alike —
 * `organizationscore` is all but empty. A page where every single row is rated would
 * misrepresent the feature as much as one where none is.
 */
function reviewsFor(organizationId: number): DemoReview[] {
  const out: DemoReview[] = [];

  // Roughly two organizations in five have been rated at all, and those carry one to four rows.
  const rated = pick(organizationId, 'rated', 10) < 4;
  const count = rated ? 1 + pick(organizationId, 'count', 4) : 0;

  for (let i = 0; i < count; i++) {
    const key = `${organizationId}#${i}`;
    // Which side the reviewer was on decides which columns they filled — the role is never a
    // flag on either score table, it IS which columns carry a value. See the memory note
    // `partner_ratings_role_from_columns`; the seed has to obey the same rule or the panel
    // would file rows under a heading their scores do not support.
    const roll = pick(key, 'role', 10);
    const asClient = roll < 6;            // they were our provider, so we rated them as a provider
    const asProvider = roll >= 4;         // overlap 4–5 produces the 'both' rows
    const score = (salt: string) => 3 + pick(key, salt, 3);   // 3–5: nobody keeps a 1-star partner

    const scores: Scores = {
      deadlines: asClient ? score('d') : null,
      cooperation: asClient ? score('c') : null,
      personabilityClient: asClient ? score('pc') : null,
      quality: asProvider ? score('q') : null,
      onTime: asProvider ? score('o') : null,
      personabilityProvider: asProvider ? score('pp') : null,
    };

    out.push({
      id: 900000 + hashOf(key) % 90000,
      rater: one(key, 'rater', RATERS),
      createdAt: isoDaysAgo(30 + pick(key, 'age', 500)),
      comment: one(key, 'comment', COMMENTS),
      role: asClient && asProvider ? 'both' : asClient ? 'client' : 'provider',
      ...scores,
      clientAverage: meanOf([scores.deadlines, scores.cooperation, scores.personabilityClient]),
      vendorAverage: meanOf([scores.quality, scores.onTime, scores.personabilityProvider]),
      mine: false,
    });
  }

  // The reader's own row, if they have rated this partner in this session. Newest, so it sorts
  // to the top the way a just-saved row should.
  const mine = myRatings.get(organizationId);
  if (mine) {
    const client = [mine.deadlines ?? null, mine.cooperation ?? null, mine.personabilityClient ?? null];
    const provider = [mine.quality ?? null, mine.onTime ?? null, mine.personabilityProvider ?? null];
    const clientAverage = meanOf(client);
    const vendorAverage = meanOf(provider);
    out.unshift({
      id: 999999,
      rater: 'You',
      createdAt: new Date().toISOString(),
      comment: mine.comment ?? '',
      role: clientAverage != null && vendorAverage != null ? 'both' : clientAverage != null ? 'client' : 'provider',
      deadlines: mine.deadlines ?? null,
      cooperation: mine.cooperation ?? null,
      personabilityClient: mine.personabilityClient ?? null,
      quality: mine.quality ?? null,
      onTime: mine.onTime ?? null,
      personabilityProvider: mine.personabilityProvider ?? null,
      clientAverage,
      vendorAverage,
      mine: true,
    });
  }

  return out;
}

/** `{average, count}` over one side, or undefined when nobody rated it. */
function summarise(reviews: DemoReview[], side: 'clientAverage' | 'vendorAverage') {
  const values = reviews.map(r => r[side]).filter((v): v is number => v != null);
  if (!values.length) return undefined;
  return {
    average: Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10,
    count: values.length,
  };
}

/**
 * `GET /ratings/organizations?organizationIds=` — imitates `Map<Long, OrganizationRating>`.
 *
 * Derived from the same {@link reviewsFor} rows the panel will show, so the column and the
 * tab can never disagree. Computing the two separately is the obvious shortcut and the
 * obvious bug: a column reading 4.2 above a tab whose rows average 3.8.
 */
export function seedOrganizationRatings(organizationIds: number[]): Record<string, any> {
  const out: Record<string, any> = {};
  for (const id of organizationIds) {
    const reviews = reviewsFor(id);
    if (!reviews.length) continue;   // unrated organizations are ABSENT, as the server leaves them
    const asClient = summarise(reviews, 'clientAverage');
    const asVendor = summarise(reviews, 'vendorAverage');
    if (asClient || asVendor) out[String(id)] = { asClient, asVendor };
  }
  return out;
}

/** `GET /ratings/organizations/{id}/reviews` — imitates `OrganizationReviews`. */
export function seedOrganizationReviews(organizationId: number): any {
  const reviews = reviewsFor(organizationId);
  return {
    asClient: summarise(reviews, 'clientAverage'),
    asVendor: summarise(reviews, 'vendorAverage'),
    reviews,
  };
}

/**
 * `GET /ratings/people?peopleIds=` — imitates `Map<Long, RoleRating>`.
 *
 * One number per person, not two: a colleague review carries a single score, and splitting
 * it would invent a distinction the data does not make.
 */
export function seedPeopleRatings(peopleIds: number[]): Record<string, any> {
  const out: Record<string, any> = {};
  for (const id of peopleIds) {
    if (pick(id, 'peer', 10) >= 3) continue;   // most colleagues have never been rated
    out[String(id)] = {
      average: Math.round((3.4 + pick(id, 'peerAvg', 16) / 10) * 10) / 10,   // 3.4–4.9
      count: 1 + pick(id, 'peerCount', 6),
    };
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Offers — GET /organizationOffers  (imitates OrganizationOffersController)
// ─────────────────────────────────────────────────────────────────────────────

/** The platform's own service vocabulary, as the create forms name them. */
const SERVICES = [
  'Translation', 'Dubbing', 'Subtitling', 'Transcription', 'Voice-over',
  'Copy-editing', 'Proofreading', 'Post-editing', 'Audio description', 'Interpreting',
];

/**
 * Raw direction codes, exactly as the server leaves them.
 *
 * Not resolved to names on purpose: the real endpoint returns codes because resolving them
 * costs two correlated sub-selects per rate, and the cell only prints a count anyway.
 */
const DIRECTIONS = [
  'eng-spa', 'spa-eng', 'eng-fra', 'fra-eng', 'eng-deu',
  'deu-eng', 'eng-por', 'eng-ita', 'eng-jpn', 'eng-zho',
];

const UNITS = ['per word', 'per hour', 'per minute', 'per page', 'per project'];

/** `GET /organizationOffers?organizationIds=` — imitates `Map<Long, OrganizationOffers>`. */
export function seedOrganizationOffers(organizationIds: number[]): Record<string, any> {
  const out: Record<string, any> = {};
  for (const id of organizationIds) {
    // An organization that publishes nothing is absent from the reply rather than present
    // with empty lists, which is what the real endpoint does and what the cell reads as
    // "Nothing published" rather than "not loaded".
    if (pick(id, 'publishes', 10) < 3) continue;
    out[String(id)] = {
      services: some(id, 'svc', SERVICES, 1 + pick(id, 'svcN', 4)),
      directions: some(id, 'dir', DIRECTIONS, 1 + pick(id, 'dirN', 5)),
      units: some(id, 'unit', UNITS, 1 + pick(id, 'unitN', 2)),
    };
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Permissions — GET /peoplePermissions  (imitates PeoplePermissionsController)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Copied verbatim from `PeoplePermissionsController.PERMISSION_LABELS`, in its order.
 *
 * The order and the column list are one thing on the server, so the copy has to be exact:
 * a label out of place would put the wrong permission in the wrong sentence.
 */
const PERMISSION_LABELS = [
  'See projects', 'Create projects', 'Edit projects',
  'See users', 'Add users', 'Edit users',
  'See tasks', 'Create tasks', 'Edit tasks', 'Act on tasks',
  'See metadata', 'Add metadata', 'Edit metadata',
  'See organizations', 'Add organizations', 'Edit organizations',
  'See contacts', 'Add contacts', 'Edit contacts',
  'See partners', 'Add partners', 'Edit partners',
  'Upload financial data', 'Set permissions', 'Send invitations',
];

/**
 * `GET /peoplePermissions?peopleIds=` — imitates `Map<Long, PersonPermissions>`.
 *
 * The real endpoint is owners-and-admins-only and answers 403 to anybody else. This one
 * cannot enforce that — it has no session to check — but the column is hidden from
 * non-admins client-side anyway, and the server will do the refusing again when it exists.
 */
export function seedPeoplePermissions(peopleIds: number[]): Record<string, any> {
  const out: Record<string, any> = {};
  for (const id of peopleIds) {
    // Between a third and all of the twenty-five, so the "12 of 25" cell has a range to show.
    const granted = PERMISSION_LABELS.filter((_, i) => pick(`${id}#${i}`, 'perm', 10) < 4 + pick(id, 'generous', 6));
    out[String(id)] = {
      granted,
      missing: PERMISSION_LABELS.filter(label => !granted.includes(label)),
      total: PERMISSION_LABELS.length,
    };
  }
  return out;
}

/**
 * `GET /peopleCapabilities?peopleIds=` — what each colleague can do.
 *
 * ⚠ No table holds these. The vocabulary is real (it is Dani's own, lifted from the 17 Sep
 * mockup); the rows against each person are not.
 *
 * A person with nothing recorded is **left out of the reply** rather than answered with an empty
 * array, which is what the real endpoint should do too: "nobody has asked them" and "they can do
 * nothing" must not arrive looking the same.
 *
 * The seed can only see the id here, so the language pool is the generic one rather than the
 * person's own languages. On dev that changes nothing — almost nobody has languages recorded.
 */
export function seedPeopleCapabilities(peopleIds: number[]): Record<string, any> {
  const out: Record<string, any> = {};
  for (const id of peopleIds) {
    const rows = seedCapabilities({ id: String(id) });
    if (rows.length) out[String(id)] = rows;
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Lookup — GET /directoryLookup  (imitates DirectoryLookupController)
// ─────────────────────────────────────────────────────────────────────────────

/** One published profile, as the confirmation card reads it. */
export interface DemoLookupResult {
  relationship: 'team' | 'contact' | 'platform' | 'self' | 'none';
  peopleId?: number;
  name?: string;
  photo?: string;
  jobTitle?: string;
  organizationId?: number;
  organization?: string;
  country?: string;
  active?: boolean;
}

/**
 * Real people, harvested from real `/people/directory` answers as they go past.
 *
 * This is the one part of the file that is **not invented**. Every row the directory returns
 * is somebody the reader may already reach, so typing a colleague's address into Add contact
 * can be answered truthfully — with their real name, organization and photo — without the
 * server. Only an address we have never seen falls through to invention.
 */
const knownPeople = new Map<string, DemoLookupResult & { organizationId?: number }>();

/** Remembers one directory row against its email. Called by the interceptor's fill step. */
export function rememberPerson(person: any, readerOrgId: number, readerPeopleId: number): void {
  const email = String(person?.email ?? '').trim().toLowerCase();
  if (!email) return;
  const id = Number(person.id);
  knownPeople.set(email, {
    relationship: id === readerPeopleId ? 'self'
      : Number(person.organizationId) === readerOrgId ? 'team'
      : 'contact',
    peopleId: id,
    name: `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() || undefined,
    photo: person.photo ?? undefined,
    jobTitle: person.organizationRole ?? undefined,
    organizationId: person.organizationId ?? undefined,
    organization: person.organizationName ?? undefined,
    country: person.country ?? undefined,
    active: person.active !== false,
  });
}

/** Invented strangers, for addresses nobody on this page has. */
const STRANGERS = [
  { name: 'Elena Marchetti', jobTitle: 'Freelance translator', organization: 'Marchetti Language Services', country: 'Italy' },
  { name: 'Jonas Weber',     jobTitle: 'Dubbing director',     organization: 'Tonhaus Berlin',              country: 'Germany' },
  { name: 'Priya Raman',     jobTitle: 'Subtitler',            organization: 'Raman Media',                 country: 'India' },
  { name: 'Diego Salazar',   jobTitle: 'Localization manager', organization: 'Andes Localization',          country: 'Chile' },
];

/**
 * `GET /directoryLookup?email=` — imitates `LookupResult`.
 *
 * Answered from {@link knownPeople} first, so a real address gives a real answer. An unknown
 * address is decided by a stable hash of itself: roughly one in three has an account on the
 * platform, the rest have none. That split is what makes both halves of Add contact — connect,
 * and invite — reachable in a demo without anybody having to memorise magic addresses.
 */
export function seedDirectoryLookup(email: string): DemoLookupResult {
  const clean = email.trim().toLowerCase();
  const known = knownPeople.get(clean);
  if (known) return known;

  // Real data only: an address nobody on the page has is answered as "no account", which leads
  // to the invite step — never to an invented stranger.
  if (INVENTED_DATA_ENABLED && pick(clean, 'hasAccount', 10) < 3) {
    const who = one(clean, 'stranger', STRANGERS);
    return {
      relationship: 'platform',
      peopleId: 800000 + (hashOf(clean) % 90000),
      ...who,
      active: true,
    };
  }
  return { relationship: 'none' };
}

// ─────────────────────────────────────────────────────────────────────────────
// Field fills — for endpoints that answer, but with fewer fields than the page reads
// ─────────────────────────────────────────────────────────────────────────────

/** Whole-hour starts, as Postgres renders a `time with time zone`. */
const HOURS_FROM = ['07:00:00+00', '08:00:00+00', '08:30:00+00', '09:00:00+00', '10:00:00+00'];
const HOURS_TO = ['15:00:00+00', '16:00:00+00', '17:00:00+00', '17:30:00+00', '18:00:00+00', '19:00:00+00'];

/** Instant-messaging services people actually list on this platform. */
const IM_TYPES = ['Skype', 'Telegram', 'WhatsApp'];

/** Street lines, for the panel's Address row. Nothing on the people endpoint ever fills it. */
const STREETS = [
  'Carrer de Mallorca 271', 'Gran Via 45, 3º', 'Rua Augusta 112',
  'Viale Monza 8', 'Kurfürstendamm 21', 'Rue de Rivoli 5',
];
const POSTCODES = ['08008', '28013', '1100-053', '20127', '10719', '75001'];

/** `YYYY-MM-DD`, `days` before today. */
function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

/** Only writes `field` when the response does not already carry a value for it. */
function fillIfAbsent(target: any, field: string, value: any): boolean {
  if (target[field] !== undefined && target[field] !== null && target[field] !== '') return false;
  target[field] = value;
  return true;
}

/** A plausible handle from a name: "Marta Ibáñez" → "marta-ibanez". */
function handleOf(first: string, last: string): string {
  return `${first}-${last}`
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')   // strip accents rather than URL-encode them
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Fills the thirteen fields `PeopleFlagsRepository` gained on 15 September and master does not
 * select: the working window, leave, who they report to, when they joined, and six links.
 *
 * Deliberately generous with working hours — around two people in three get them, where in
 * real data almost nobody has ever set theirs. Without that the availability clock, the Now
 * card and four facets have nothing at all to show, and a feature that renders empty cannot
 * be reviewed. Everything else stays at a realistic frequency.
 *
 * `reportsToName` is chosen from the same response page, so the manager named is somebody who
 * actually exists in the same organization rather than an invented person.
 */
export function fillPerson(person: any, colleagues: any[] = []): boolean {
  if (!person || person.id == null) return false;
  const id = Number(person.id);
  let touched = false;
  const set = (field: string, value: any) => { touched = fillIfAbsent(person, field, value) || touched; };

  // ── The working window ──────────────────────────────────────────────
  if (pick(id, 'hasHours', 10) < 7) {
    set('workingHoursFrom', one(id, 'from', HOURS_FROM));
    set('workingHoursTo', one(id, 'to', HOURS_TO));
  }
  // Leave is rare, and only for somebody who has a working day at all: a person with no hours
  // but a holiday booked made the list say "Hours not set" while the panel's Now card showed
  // their leave — the same person, answered two different ways on one screen.
  if (person.workingHoursFrom && pick(id, 'onLeave', 20) === 0) {
    const starts = pick(id, 'leaveStart', 20) - 6;             // a few days either side of today
    set('vacationsFrom', isoDaysAgo(starts));
    set('vacationsTo', isoDaysAgo(starts - 7 - pick(id, 'leaveLen', 10)));
  }

  // ── Who they report to ──────────────────────────────────────────────
  // Only within the same organization, and never themselves; a manager from another company
  // would be nonsense rather than demo data.
  const sameOrg = colleagues.filter(c => c && c.id !== person.id && c.organizationId === person.organizationId);
  if (sameOrg.length && pick(id, 'hasManager', 10) < 6) {
    const boss = sameOrg[pick(id, 'manager', sameOrg.length)];
    set('reportsToId', Number(boss.id));
    set('reportsToName', `${boss.firstName ?? ''} ${boss.lastName ?? ''}`.trim() || null);
  }

  // ── Joined the platform ─────────────────────────────────────────────
  // Derived from the REAL `inOrganizationSince`, which master does send, and always earlier
  // than it: you cannot join a company on the platform before you have an account.
  if (person.inOrganizationSince) {
    const joinedOrg = new Date(person.inOrganizationSince);
    if (!Number.isNaN(joinedOrg.getTime())) {
      joinedOrg.setDate(joinedOrg.getDate() - pick(id, 'joined', 400));
      set('joinedAt', joinedOrg.toISOString().slice(0, 10));
    }
  }

  // ── Where they are ──────────────────────────────────────────────────
  // The people endpoint carries city, state and country but never a street, so the panel's
  // Address row would be permanently absent. About a third of people get one.
  if (pick(id, 'hasAddress', 10) < 4) {
    set('addressLine1', one(id, 'street', STREETS));
    set('addressLine2', one(id, 'postcode', POSTCODES));
  }

  // ── Links ───────────────────────────────────────────────────────────
  const handle = handleOf(String(person.firstName ?? 'a'), String(person.lastName ?? 'b'));
  const domain = handleOf(String(person.organizationName ?? 'example'), '') || 'example';
  if (pick(id, 'hasLinkedin', 10) < 5) set('linkedin', `linkedin.com/in/${handle}`);
  if (pick(id, 'hasWeb', 10) < 3) set('web', `www.${domain}.com`);
  if (pick(id, 'hasTwitter', 10) < 2) set('twitter', `x.com/${handle}`);
  if (pick(id, 'hasFacebook', 10) < 2) set('facebook', `facebook.com/${handle}`);
  if (pick(id, 'hasIm', 10) < 3) {
    set('imType', one(id, 'imType', IM_TYPES));
    set('im', handle);
  }

  // ── What they are working on this week ──────────────────────────────
  // `people.actualstatus varchar(255)` EXISTS and is mapped on the People entity in `common`;
  // nothing selects it into the directory response. So this is a field to add, not a table to
  // design — see directories-BACKEND-CONTRACT.md §2b. Around one person in three writes one.
  // Guarded rather than passed straight to `set`: fillIfAbsent counts writing `undefined` as a
  // fill, which would make the "Demo data" pill claim a status message nobody has.
  const status = seedStatusMessage(String(id));
  if (status && fillIfAbsent(person, 'statusMessage', status)) {
    touched = true;
    noteDemoData('status messages');
  }

  // ── Also offering services on the marketplace ───────────────────────
  // ⚠ Nothing links a contact to a listing, so this is the thinnest claim in the file. It is
  // read only on the External tab; on a colleague the field is set and never rendered, which is
  // what the real endpoint would do too.
  if (seedListedOnMarketplace(String(id)) && fillIfAbsent(person, 'onMarketplace', true)) {
    touched = true;
    noteDemoData('marketplace overlap');
  }

  return touched;
}

/**
 * Fills the four fields `OrganizationAttributesRepository` gained on 15 September: the
 * organization's own opening hours and its shutdown window.
 *
 * Half of partners get hours. An organization carries no time zone, so these are never read
 * as "open now" — only as the window itself, which is the honest ceiling on the data.
 */
export function fillPartner(org: any): boolean {
  if (!org || org.id == null) return false;
  const id = Number(org.id);
  let touched = false;
  const set = (field: string, value: any) => { touched = fillIfAbsent(org, field, value) || touched; };

  if (pick(id, 'orgHasHours', 10) < 5) {
    set('businessHoursFrom', one(id, 'orgFrom', HOURS_FROM));
    set('businessHoursTo', one(id, 'orgTo', HOURS_TO));
  }
  if (pick(id, 'orgClosed', 20) === 0) {
    const starts = pick(id, 'orgLeaveStart', 20) - 6;
    set('vacationsFrom', isoDaysAgo(starts));
    set('vacationsTo', isoDaysAgo(starts - 10));
  }
  return touched;
}

/**
 * Names a task's project when the server leaves it blank.
 *
 * ⚠ **Not invented, and not a seed.** `task.projectname` is a denormalized column nothing has
 * ever written — empty on all 15,069 rows on Dev — which is why the panel's Project column has
 * been blank since it was added. `projectId` on the same row is real. Printing the real id
 * beats printing nothing, and naming an invented project against a real id would be worse than
 * either. When the API resolves the name, this steps aside.
 */
export function fillTask(task: any): boolean {
  if (!task || task.projectId == null) return false;
  return fillIfAbsent(task, 'projectName', `Project ${task.projectId}`);
}

/**
 * Names a marketplace listing's credential from the language it already carries.
 *
 * ⚠ **Not invented.** `DirectoryProspectController` computes exactly this, on exactly this
 * input — `nativeLanguage != null && !isEmpty ? "Native speaker" : null` — and master simply
 * does not put the result on the LIST rows, only on the detail. Recomputing it client-side
 * repeats the server's own rule over the server's own data.
 *
 * **The four fields next to it are deliberately left empty**: `disciplines`, `software`,
 * `idVerified` and `emailVerified` are real facts about real, named people imported from
 * ProZ. They are in the database and master's detail endpoint returns them; only the list
 * omits them. Inventing a discipline — or worse, an identity verification — against a real
 * professional's name would not be demo data, it would be a false claim about a person.
 * Those columns stay empty until the list carries them.
 */
export function fillProspect(prospect: any): boolean {
  if (!prospect) return false;
  let touched = false;

  // The one real derivation in this function: a listing whose native language is set is, by
  // definition, a native speaker of it. Kept behind the same guard it always had.
  if (prospect.nativeLanguage) {
    touched = fillIfAbsent(prospect, 'credential', 'Native speaker') || touched;
  }

  // ── The GlotWise overlap ────────────────────────────────────────────
  // ⚠ Invented, and the thinnest claim on the page: nothing in the database links an imported
  // ProZ listing to a GlotWise account or to one of your contacts. About one listing in eight
  // gets an account, and a third of those are already a contact. Every mark says so on hover.
  if (INVENTED_DATA_ENABLED && prospect.id != null) {
    const account = seedGlotwiseAccount(String(prospect.id));
    if (account && fillIfAbsent(prospect, 'glotwise', account)) {
      fillIfAbsent(prospect, 'inMyContacts', seedAlreadyAContact(String(prospect.id), true));
      touched = true;
      noteDemoData('marketplace overlap');
    }
  }

  return touched;
}
